import { Routes, Route, Navigate } from 'react-router-dom';
import AppShell from '@/components/layout/AppShell';
import LegacyRedirect, { LEGACY_REDIRECTS } from '@/components/layout/LegacyRedirect';
import SurfaceGate from '@/components/layout/SurfaceGate';
import RequireAuth from '@/components/auth/RequireAuth';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import Home from '@/pages/Home';
import RoleLanding from '@/components/layout/RoleLanding';
import Production from '@/pages/Production';
import Sources from '@/pages/Sources';
import Channels from '@/pages/Channels';
import Ads from '@/pages/Ads';
import VOD from '@/pages/VOD';
import Player from '@/pages/Player';
import Analytics from '@/pages/Analytics';
import SitesPage from '@/pages/sites/SitesPage';
import Social from '@/pages/Social';
import DRM from '@/pages/DRM';
import Events from '@/pages/Events';
import EventViewer from '@/pages/EventViewer';
import GuestJoin from '@/pages/GuestJoin';
import Account from '@/pages/Account';
import RecordingsPage from '@/pages/processing/RecordingsPage';
import RecordingDetailPage from '@/pages/processing/RecordingDetailPage';
import JobsPage from '@/pages/processing/JobsPage';
import ClipsReviewPage from '@/pages/processing/ClipsReviewPage';
import ProcessingSettingsPage from '@/pages/processing/ProcessingSettingsPage';

// Routes follow the surfaces registry (src/surfaces.ts). SurfaceGate shows
// "not enabled" on the pages of a surface the acting org doesn't have (#1160).
function Layout() {
  return (
    <RequireAuth>
      <AppShell>
        <SurfaceGate>
          <Routes>
            {/* Home (#1155); `/` lands by role (lib/landing.ts) */}
            <Route path="/" element={<RoleLanding />} />
            <Route path="/home" element={<Home />} />

            {/* Production — placeholder until #1161 */}
            <Route path="/production" element={<Production />} />

            {/* Audience */}
            <Route path="/audience" element={<Navigate to="/audience/events" replace />} />
            <Route path="/audience/events" element={<Events />} />
            <Route path="/audience/events/:eventId" element={<Events />} />

            {/* Streaming */}
            <Route path="/streaming" element={<Navigate to="/streaming/sources" replace />} />
            <Route path="/streaming/sources" element={<Sources />} />
            <Route path="/streaming/sources/:sourceId" element={<Sources />} />
            <Route path="/streaming/channels" element={<Channels />} />
            <Route path="/streaming/channels/:channelId" element={<Channels />} />
            <Route path="/streaming/vod" element={<VOD />} />
            <Route path="/streaming/ads" element={<Ads />} />
            <Route path="/streaming/drm" element={<DRM />} />
            <Route path="/streaming/player" element={<Player />} />
            <Route path="/streaming/analytics" element={<Analytics />} />

            {/* Media */}
            <Route path="/media" element={<Navigate to="/media/recordings" replace />} />
            <Route path="/media/recordings" element={<RecordingsPage />} />
            <Route path="/media/recordings/:sourceId" element={<RecordingDetailPage />} />
            <Route path="/media/jobs" element={<JobsPage />} />
            <Route path="/media/clips" element={<ClipsReviewPage />} />
            <Route path="/media/social" element={<Social />} />
            <Route path="/media/settings" element={<ProcessingSettingsPage />} />

            {/* Sites */}
            <Route path="/sites" element={<SitesPage />} />
            <Route path="/sites/machines" element={<SitesPage />} />
            <Route path="/sites/:siteId" element={<SitesPage />} />

            {/* Organization */}
            <Route path="/org" element={<Navigate to="/org/account" replace />} />
            <Route path="/org/account" element={<Account />} />

            {/* Operations moved to corp-console's Platform health (#1156):
                /ops is gone for everyone, superusers included. */}
            <Route path="/ops" element={<Navigate to="/home" replace />} />

            {/* Pre-surfaces paths: bookmarks and emailed links keep working */}
            {LEGACY_REDIRECTS.map(({ from, to }) => (
              <Route key={from} path={from} element={<LegacyRedirect to={to} />} />
            ))}
          </Routes>
        </SurfaceGate>
      </AppShell>
    </RequireAuth>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Public: guests are not JSS users. The page talks only to the
          unauthenticated participant routes the BFF already proxies. */}
      <Route path="/join/:slug" element={<GuestJoin />} />
      {/* Public: a public event's share link works signed out (#1177).
          EventViewer asks a signed-out caller to sign in for a private event,
          and event-manager decides what each caller sees (#1175). */}
      <Route path="/event/:slug" element={<EventViewer />} />
      <Route path="/*" element={<Layout />} />
    </Routes>
  );
}
